import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mic, MicOff, Video, VideoOff, Star, MessageSquare, Play, Sparkles, Clock, Youtube, Settings as SettingsIcon, ExternalLink, Film, ShieldCheck } from "lucide-react";
import { getCreator, startConversation, createOrder } from "../lib/api";
import { formatNumber, timeAgo, GAMES, SERVICES, DELIVERY_SCOPE_LABEL } from "../lib/constants";
import { extractYouTubeId, youtubeThumb, youtubeEmbed } from "../lib/youtube";
import InfoTip from "../components/InfoTip";
import SoftwareAccessSection from "../components/SoftwareAccessSection";
import VerifiedBadge from "../components/VerifiedBadge";
import { useUser } from "../context/UserContext";
import usePageMeta from "../lib/usePageMeta";
import { toast } from "sonner";

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

export default function CreatorProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, openChat } = useUser();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderForm, setShowOrderForm] = useState(false);

  const displayName = creator?.username || creator?.name || id;
  usePageMeta({
    title: creator ? `${displayName} — Creator on CheatCraft` : "Creator on CheatCraft",
    description: creator
      ? `${displayName} · ${(creator.games || []).slice(0, 3).join(", ") || "Media creator"} · Browse portfolio, ratings and order custom video content on CheatCraft.`
      : "Discover a trusted media creator on CheatCraft.",
    canonicalPath: `/creators/${id}`,
    ogImage: creator?.avatar_url || undefined,
  });

  useEffect(() => {
    setLoading(true);
    getCreator(id)
      .then(setCreator)
      .catch(() => setCreator(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleContact = async () => {
    if (user?.role !== "company") {
      toast.error("Switch to a company account to contact creators.");
      return;
    }
    try {
      const conv = await startConversation(id);
      openChat(conv);
    } catch {
      toast.error("Could not open conversation");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-16 text-[#606070]">Loading…</div>;
  if (!creator) return <div className="max-w-7xl mx-auto px-6 py-16 text-white">Creator not found.</div>;

  return (
    <div data-testid="creator-profile-page" className="pb-16">
      <div className="relative h-56 md:h-72 overflow-hidden">
        {creator.banner_url && (
          <img src={creator.banner_url} alt="" className="w-full h-full object-cover opacity-50" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#06060c]/70 to-[#06060c]" />
        <div className="absolute inset-0 cc-grid-bg opacity-30" />
        <button
          data-testid="profile-back-btn"
          onClick={() => nav(-1)}
          className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#15152a] bg-[#0a0a14]/80 backdrop-blur text-sm text-white hover:border-[#1f1f3a]"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-20 relative">
        <div className="cc-surface rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 relative">
          {creator.featured && (
            <span className="cc-featured-badge absolute top-4 right-4 inline-flex items-center gap-1">
              <Sparkles size={10} /> Featured
            </span>
          )}
          <div className="relative">
            <div className="w-28 h-28 rounded-2xl overflow-hidden border border-[#15152a] bg-[#06060c]">
              {creator.avatar_url ? (
                <img src={creator.avatar_url} alt={creator.username} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-5xl text-[#00ff88]">{creator.glyph}</div>
              )}
            </div>
            <span className={`cc-status-dot ${creator.status} absolute bottom-1 right-1 ring-4 ring-[#0a0a14]`} />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[#00ff88] text-xl">{creator.glyph}</span>
              <h1 className="text-3xl font-semibold tracking-tight text-white">{creator.username}</h1>
              <VerifiedBadge verified={creator.verified} testIdSuffix="profile" size="md" />
              <span className="cc-chip ml-1 capitalize">{creator.status}</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#a0a0b0]">
              <span className="font-mono">{formatNumber(creator.subscribers)} subscribers</span>
              <span className="flex items-center gap-1 font-mono"><Star size={12} className="fill-[#ffaa00] text-[#ffaa00]" />{creator.rating} · {creator.reviews_count} reviews</span>
              <span className="font-mono">{creator.languages.join(" · ")}</span>
            </div>
            <p className="text-sm text-[#a0a0b0] mt-4 max-w-2xl leading-relaxed">{creator.bio}</p>
          </div>

          <div className="md:text-right md:min-w-[220px]">
            <div className="cc-overline">Price / video</div>
            <div className="font-mono text-4xl text-[#00ff88] mt-1" style={{ textShadow: "0 0 20px rgba(0,255,136,0.35)" }}>${creator.price}</div>
            <div className="flex md:flex-col gap-2 mt-4">
              {user?.role === "creator" && user?.id === creator.id ? (
                <Link
                  to="/settings"
                  data-testid="profile-edit-btn"
                  className="cc-btn-primary rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"
                >
                  <SettingsIcon size={14} /> Edit Profile
                </Link>
              ) : (
                <>
                  <button
                    data-testid="profile-contact-btn"
                    onClick={handleContact}
                    className="rounded-lg px-4 py-2.5 border border-[#15152a] bg-[#0d0d1a] hover:border-[#1f1f3a] text-white text-sm inline-flex items-center gap-2"
                  >
                    <MessageSquare size={14} /> Contact
                  </button>
                  <button
                    data-testid="profile-order-btn"
                    onClick={() => setShowOrderForm(true)}
                    className="cc-btn-primary rounded-lg px-4 py-2.5 text-sm"
                  >
                    Place Order
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="space-y-6">
            <Section title="Equipment">
              <InfoRow label="Video Quality" value={creator.quality} mono />
              <InfoRow label="Microphone" value={<BadgeBool on={creator.has_mic} IconOn={Mic} IconOff={MicOff} />} />
              <InfoRow label="Facecam" value={<BadgeBool on={creator.has_facecam} IconOn={Video} IconOff={VideoOff} />} />
              <InfoRow label="Software" value={creator.editing_software?.join(", ") || "—"} mono />
            </Section>

            <Section title="Delivery">
              <InfoRow
                label="Scope"
                value={
                  <span className="inline-flex items-center gap-1 text-xs text-[#00ff88] border border-[#00ff88]/30 bg-[#00ff88]/5 rounded px-2 py-0.5">
                    {creator.delivery_scope === "full" && <Youtube size={11} />}
                    {DELIVERY_SCOPE_LABEL[creator.delivery_scope] || "Record + Edit"}
                  </span>
                }
              />
              <InfoRow
                label={
                  <span className="inline-flex items-center gap-1.5">
                    Max delivery time
                    <InfoTip
                      testId="profile-sla-tip"
                      text={`If ${creator.username} doesn't deliver your video within ${creator.avg_delivery_days ?? 7} day${(creator.avg_delivery_days ?? 7) > 1 ? "s" : ""}, you can claim a full refund of your payment from the order page — no questions asked.`}
                    />
                  </span>
                }
                value={<span className="font-mono inline-flex items-center gap-1"><Clock size={11} className="text-[#00ff88]" />{creator.avg_delivery_days ?? 7}d max</span>}
              />
            </Section>

            {/* Refund-protection callout */}
            <div data-testid="profile-refund-callout" className="cc-surface rounded-2xl p-5 border border-[#00ff88]/20 bg-[#00ff88]/5">
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-[#00ff88] shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-white font-medium mb-1">Money-back guarantee</div>
                  <div className="text-[12px] text-[#a0a0b0] leading-relaxed">
                    Funds stay in escrow until you approve. If {creator.username} doesn&apos;t deliver within{" "}
                    <span className="text-[#00ff88] font-mono">{creator.avg_delivery_days ?? 7} day{(creator.avg_delivery_days ?? 7) > 1 ? "s" : ""}</span>, you get a full refund.
                  </div>
                </div>
              </div>
            </div>

            <Section title="Games">
              <div className="flex flex-wrap gap-1.5">
                {creator.games.map((g) => <span key={g} className="cc-chip">{g}</span>)}
              </div>
            </Section>

            <Section title="Services">
              <div className="flex flex-wrap gap-1.5">
                {creator.services.map((s) => (
                  <span key={s} className="text-[11px] uppercase tracking-widest text-[#00ff88]/80 border border-[#00ff88]/20 bg-[#00ff88]/5 rounded px-2 py-1">{s}</span>
                ))}
              </div>
            </Section>

            {(creator.upsells || []).filter((u) => u.enabled !== false).length > 0 && (
              <Section title="Available Add-ons">
                <div data-testid="profile-upsells" className="space-y-1.5">
                  {(creator.upsells || []).filter((u) => u.enabled !== false).map((u) => (
                    <div
                      key={u.id}
                      data-testid={`profile-upsell-${u.id}`}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a] transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-white truncate">{u.name}</div>
                        {u.description && <div className="text-[10px] text-[#606070] truncate">{u.description}</div>}
                      </div>
                      <div className="text-xs font-mono text-[#00ff88] shrink-0">+${u.price}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-[#606070] mt-2 italic">Pick any of these when you place the order.</div>
              </Section>
            )}

            <Section title="Availability">
              <MiniCalendar busy={creator.status === "busy"} />
            </Section>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <Section title="Portfolio">
              {(creator.portfolio || []).length === 0 && (
                <div className="text-xs text-[#606070]">No portfolio links yet.</div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                {(creator.portfolio || []).map((p, i) => (
                  <PortfolioVideo key={i} index={i} item={p} />
                ))}
              </div>
            </Section>

            <Section title={`Reviews (${creator.reviews?.length || 0})`}>
              <div className="space-y-3">
                {(creator.reviews || []).map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="cc-elevated rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-white">{r.company_name}</div>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={12} className={i < r.rating ? "fill-[#ffaa00] text-[#ffaa00]" : "text-[#303050]"} />
                        ))}
                      </div>
                    </div>
                    <div className="text-sm text-[#a0a0b0] mt-2">{r.comment}</div>
                    <div className="text-[10px] text-[#606070] mt-2">{timeAgo(r.created_at)}</div>
                  </div>
                ))}
                {(!creator.reviews || creator.reviews.length === 0) && (
                  <div className="text-xs text-[#606070]">No reviews yet.</div>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>

      {showOrderForm && (
        <OrderDialog creator={creator} onClose={() => setShowOrderForm(false)} />
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="cc-surface rounded-2xl p-6">
    <div className="cc-overline mb-4">{title}</div>
    <div>{children}</div>
  </section>
);

const InfoRow = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#15152a] last:border-0">
    <span className="text-[11px] text-[#606070] uppercase tracking-widest">{label}</span>
    <span className={`text-sm text-white ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

const BadgeBool = ({ on, IconOn, IconOff }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded border ${on ? "border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/5" : "border-[#15152a] text-[#606070]"}`}>
    {on ? <IconOn size={11} /> : <IconOff size={11} />}
    {on ? "Yes" : "No"}
  </span>
);

const MiniCalendar = ({ busy }) => {
  const days = Array.from({ length: 28 });
  return (
    <div>
      <div className="grid grid-cols-7 gap-1.5 text-[10px] text-[#606070] mb-1">
        {DAYS.map((d, i) => <div key={i} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((_, i) => {
          const unavailable = busy ? (i % 3 === 0) : (i % 7 === 5 || i % 7 === 6);
          return (
            <div
              key={i}
              className={`aspect-square rounded ${unavailable ? "bg-[#ff4444]/10 border border-[#ff4444]/20" : "bg-[#00ff88]/5 border border-[#00ff88]/15"}`}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] text-[#606070]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-[#00ff88]/40" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-[#ff4444]/40" /> Busy</span>
      </div>
    </div>
  );
};

const OrderDialog = ({ creator, onClose }) => {
  const nav = useNavigate();
  const { user } = useUser();
  const enabledUpsells = (creator.upsells || []).filter((u) => u.enabled !== false);
  const [form, setForm] = useState({
    content_type: creator.services[0] || "Review",
    game: creator.games[0] || "Fortnite",
    brief: "",
    deadline: new Date(Date.now() + (creator.avg_delivery_days ?? 7) * 864e5).toISOString().slice(0, 10),
    price: creator.price,
    upsell_ids: [],
    payment_method: "ltc",
    software: { method: "later", url: "", license_key: "", license_duration: "", instructions: "" },
  });
  const [submitting, setSubmitting] = useState(false);


  const toggleUpsell = (id) =>
    setForm((f) => ({
      ...f,
      upsell_ids: f.upsell_ids.includes(id) ? f.upsell_ids.filter((x) => x !== id) : [...f.upsell_ids, id],
    }));

  const upsellsTotal = enabledUpsells
    .filter((u) => form.upsell_ids.includes(u.id))
    .reduce((s, u) => s + Number(u.price || 0), 0);
  const subtotal = Number(form.price || 0) + upsellsTotal;
  const fee = Math.round(subtotal * 0.1);
  const total = subtotal + fee;

  const submit = async () => {
    if (user?.role !== "company") {
      toast.error("Only a company account can place orders.");
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder({
        creator_id: creator.id,
        content_type: form.content_type,
        game: form.game,
        brief: form.brief,
        deadline: form.deadline,
        price: Number(form.price),
        upsell_ids: form.upsell_ids,
        payment_method: form.payment_method,
        software_method: form.software.method,
        software_url: form.software.url || undefined,
        license_key: form.software.license_key || undefined,
        license_duration: form.software.license_duration || undefined,
        software_instructions: form.software.instructions || undefined,
      });
      onClose();
      if (form.payment_method === "ltc") {
        toast.success(`Order ${order.id} created · pay with LTC to start`);
        nav(`/pay/${order.id}`);
      } else {
        toast.success(`Order ${order.id} created & paid into escrow`);
        nav(`/orders/${order.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="order-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-2xl my-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="cc-overline">New order</div>
            <h3 className="text-xl font-semibold text-white mt-1">{creator.glyph} {creator.username}</h3>
          </div>
          <button onClick={onClose} className="text-[#606070] hover:text-white" data-testid="order-dialog-close">✕</button>
        </div>

        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 space-y-3">
            <FormSelect label="Content Type" value={form.content_type} options={creator.services.length ? creator.services : SERVICES} onChange={(v) => setForm({ ...form, content_type: v })} testId="order-content-type" />
            <FormSelect label="Game" value={form.game} options={creator.games.length ? creator.games : GAMES} onChange={(v) => setForm({ ...form, game: v })} testId="order-game" />
            <div>
              <div className="cc-overline mb-1">Brief</div>
              <textarea
                data-testid="order-brief" rows={4} value={form.brief}
                onChange={(e) => setForm({ ...form, brief: e.target.value })}
                placeholder="Describe expectations, length, tone, deliverables…"
                className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] cc-focus-ring"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="cc-overline mb-1">Deadline (creator commits to)</div>
                <div data-testid="order-deadline-display" className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white font-mono">
                  Within {creator.avg_delivery_days ?? 7} days
                </div>
                <div className="text-[10px] text-[#606070] mt-1">Set by the creator. Full refund if missed.</div>
              </div>
              <div>
                <div className="cc-overline mb-1">Base price</div>
                <div data-testid="order-price-display" className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-[#00ff88] font-mono">
                  ${creator.price}
                </div>
                <div className="text-[10px] text-[#606070] mt-1">Set by the creator on their profile.</div>
              </div>
            </div>

            <div className="pt-2">
              <div className="cc-overline mb-2">Payment method</div>
              <div data-testid="order-payment-toggle" className="grid grid-cols-2 gap-2">
                <PayMethodCard
                  active={false}
                  onClick={() => toast.info("Credit card support is coming soon — pay with Litecoin for now.")}
                  testId="pay-method-card"
                  emoji="💳"
                  title="Credit Card"
                  sub="SOON"
                  disabled
                />
                <PayMethodCard
                  active={form.payment_method === "ltc"}
                  onClick={() => setForm({ ...form, payment_method: "ltc" })}
                  testId="pay-method-ltc"
                  emoji="⚡"
                  title="Litecoin (LTC)"
                  sub="On-chain · ~2 min"
                />
              </div>
            </div>

            {enabledUpsells.length > 0 && (
              <div className="pt-2">
                <div className="cc-overline mb-2 inline-flex items-center gap-1.5">
                  <Sparkles size={11} className="text-[#00ff88]" /> Add-ons by {creator.username}
                </div>
                <div data-testid="order-upsells" className="space-y-2">
                  {enabledUpsells.map((u) => {
                    const checked = form.upsell_ids.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        data-testid={`order-upsell-${u.id}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          checked ? "border-[#00ff88]/50 bg-[#00ff88]/5" : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleUpsell(u.id)}
                          data-testid={`order-upsell-checkbox-${u.id}`}
                          className="mt-1 accent-[#00ff88]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-medium">{u.name}</div>
                          {u.description && <div className="text-[11px] text-[#a0a0b0] mt-0.5 leading-relaxed">{u.description}</div>}
                        </div>
                        <div className="text-sm font-mono text-[#00ff88]">+${u.price}</div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <SoftwareAccessSection
              value={form.software}
              onChange={(software) => setForm((f) => ({ ...f, software }))}
            />
          </div>

          {/* Order summary */}
          <aside data-testid="order-summary" className="md:col-span-2 cc-surface rounded-xl p-4 self-start">
            <div className="cc-overline mb-3">Order summary</div>
            <SummaryLine label="Base video" value={`$${form.price || 0}`} />
            {enabledUpsells.filter((u) => form.upsell_ids.includes(u.id)).map((u) => (
              <SummaryLine key={u.id} label={`+ ${u.name}`} value={`$${u.price}`} accent />
            ))}
            <div className="border-t border-[#15152a] my-2" />
            <SummaryLine label="Subtotal · creator earns" value={`$${subtotal}`} bold />
            <SummaryLine label="Platform fee (10%)" value={`$${fee}`} dim />
            <div className="border-t border-[#15152a] my-2" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-white font-semibold">You pay</span>
              <span data-testid="order-total" className="text-2xl font-mono text-[#00ff88]">${total}</span>
            </div>
            <div className="text-[10px] text-[#606070] mt-3 leading-relaxed inline-flex items-start gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ffaa00] mt-1.5 shrink-0" />
              <span>Funds held in escrow until you approve the delivery.</span>
            </div>
            <div data-testid="order-refund-clause" className="mt-3 p-2.5 rounded-md border border-[#00ff88]/25 bg-[#00ff88]/5">
              <div className="text-[11px] text-white font-medium mb-0.5">Money-back guarantee</div>
              <div className="text-[10px] text-[#a0a0b0] leading-relaxed">
                If {creator.username} doesn&apos;t deliver within their max delivery time of{" "}
                <span className="text-[#00ff88] font-mono">{creator.avg_delivery_days ?? 7}d</span>, you get a full refund of ${total}.
              </div>
            </div>
          </aside>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button
            data-testid="order-submit-btn"
            disabled={submitting || !form.brief.trim()}
            onClick={submit}
            className="cc-btn-primary rounded-lg px-5 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Creating…" : (form.payment_method === "ltc" ? `Pay $${total} with LTC` : `Pay $${total} into Escrow`)}
          </button>
        </div>
      </div>
    </div>
  );
};

const SummaryLine = ({ label, value, accent, bold, dim }) => (
  <div className="flex items-center justify-between py-1">
    <span className={`text-xs ${bold ? "text-white font-semibold" : dim ? "text-[#606070]" : "text-[#a0a0b0]"}`}>{label}</span>
    <span className={`text-xs font-mono ${accent ? "text-[#00ff88]" : bold ? "text-white" : dim ? "text-[#606070]" : "text-white"}`}>{value}</span>
  </div>
);

const PayMethodCard = ({ active, onClick, testId, emoji, title, sub, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    aria-disabled={disabled}
    tabIndex={disabled ? -1 : 0}
    className={`relative text-left rounded-lg p-3 border transition-all ${
      disabled
        ? "border-[#15152a] bg-[#06060c]/40 opacity-60 cursor-not-allowed"
        : active
          ? "border-[#00ff88]/40 bg-[#00ff88]/5"
          : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"
    }`}
  >
    {disabled && (
      <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold text-[#ffaa00] bg-[#ffaa00]/10 border border-[#ffaa00]/30 rounded-full px-1.5 py-0.5">
        SOON
      </span>
    )}
    <div className="flex items-center gap-2 mb-0.5">
      <span className="text-base">{emoji}</span>
      <span className={`text-sm font-medium ${disabled ? "text-[#606070]" : active ? "text-[#00ff88]" : "text-white"}`}>{title}</span>
    </div>
    <div className="text-[10px] text-[#606070]">{sub}</div>
  </button>
);

const FormSelect = ({ label, value, options, onChange, testId }) => (
  <div>
    <div className="cc-overline mb-1">{label}</div>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white cc-focus-ring">
      {options.map((o) => <option key={o} value={o} className="bg-[#06060c]">{o}</option>)}
    </select>
  </div>
);

const BACKEND = "";  // kept as empty for compatibility; URLs are same-origin
const isVideoUrl = (u) => /\.(mp4|webm|mov|mkv|avi)(\?|$)/i.test(u || "");
const isImageUrl = (u) => /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(u || "");
const absoluteUrl = (u) => u;

const PortfolioVideo = ({ item, index }) => {
  const [playing, setPlaying] = useState(false);
  const ytId = extractYouTubeId(item.url);
  const directVideo = !ytId && isVideoUrl(item.url);
  const directImage = !ytId && isImageUrl(item.url);
  const thumb = item.thumb || (ytId ? youtubeThumb(ytId, "hqdefault") : (directImage ? absoluteUrl(item.url) : null));
  const title = item.title && !/^Portfolio link #\d+$/.test(item.title)
    ? item.title
    : (ytId ? `youtu.be/${ytId}` : (directVideo ? "Uploaded video" : (directImage ? "Uploaded image" : (item.url || "Untitled"))));

  return (
    <div data-testid={`portfolio-item-${index}`} className="cc-elevated rounded-xl overflow-hidden cc-card-hover">
      <div className="relative aspect-video bg-[#06060c] group">
        {playing && ytId ? (
          <iframe
            data-testid={`portfolio-iframe-${index}`}
            src={youtubeEmbed(ytId)}
            title={title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : directVideo ? (
          <video
            data-testid={`portfolio-video-${index}`}
            src={absoluteUrl(item.url)}
            controls
            preload="metadata"
            className="w-full h-full object-cover bg-black"
          />
        ) : thumb ? (
          <>
            <img
              src={thumb}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                if (ytId && !e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = youtubeThumb(ytId, "mqdefault");
                } else {
                  e.currentTarget.style.display = "none";
                }
              }}
            />
            {!directImage && (
              <button
                data-testid={`portfolio-play-${index}`}
                onClick={(e) => { e.preventDefault(); if (ytId) setPlaying(true); else window.open(item.url, "_blank", "noopener"); }}
                className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-colors"
                aria-label="Play video"
              >
                <span className="w-14 h-14 rounded-full bg-[#00ff88]/95 text-[#06060c] flex items-center justify-center shadow-[0_6px_30px_-6px_rgba(0,255,136,0.55)] group-hover:scale-105 transition-transform">
                  <Play size={22} className="ml-1" fill="currentColor" />
                </span>
              </button>
            )}
          </>
        ) : (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="absolute inset-0 flex flex-col items-center justify-center text-[#606070] gap-2 hover:text-[#00ff88] transition-colors"
          >
            <Film size={28} />
            <span className="text-[11px] font-mono">Open link ↗</span>
          </a>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2">
          <div className="text-sm text-white truncate flex-1">{title}</div>
          {item.url && !directVideo && !directImage && (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`portfolio-open-${index}`}
              className="text-[#606070] hover:text-[#00ff88]"
              aria-label="Open on YouTube"
            >
              <ExternalLink size={13} />
            </a>
          )}
          {(directVideo || directImage) && (
            <a
              href={absoluteUrl(item.url)}
              target="_blank"
              rel="noreferrer"
              data-testid={`portfolio-open-${index}`}
              className="text-[#606070] hover:text-[#00ff88]"
              aria-label="Open file"
            >
              <ExternalLink size={13} />
            </a>
          )}
        </div>
        <div className="flex items-center justify-between mt-1 text-[11px] text-[#606070]">
          <span>{timeAgo(item.at)}</span>
          {item.views > 0 && <span>{formatNumber(item.views)} views</span>}
        </div>
      </div>
    </div>
  );
};
